import { List, Range } from 'immutable';
import {
  FREEFORM,
  isMultiChoice,
  isPool,
  FIB_EMBEDDING_MARKER,
} from 'app/constants/enums/QuizType';
import { SIMPLE } from 'app/constants/enums/VocabType';
import { MEDIUM } from 'app/constants/MediaSizes';
import { PLAIN } from 'app/constants/enums/ParagraphStyle';
import { createOptimisticAction } from 'app/remote/generic/actions';
import { BASE_ACTION } from 'app/remote/generic/constants';
import { batchActions } from 'redux-batched-actions';
import {
  findPreviousQuizInExercise,
  getEntityOfType,
  getEntityOfTypeOrThrow,
  makeGetNumQuizMarkers,
} from 'app/models/entities/selectors';
import { findNumOccurrences } from 'app/utils/text';
import {
  Answer,
  ArticleSection,
  Banner,
  BannerContext,
  BannerImage,
  BannerProduct,
  BannerTranslation,
  CommonText,
  DialogEntry,
  DialogSection,
  DialogSentence,
  DialogSentenceQuiz,
  Exercise,
  Image,
  Issue,
  Lesson,
  LocalWord,
  MediaEntry,
  MediaGroup,
  MediaSection,
  OauthApplication,
  Paragraph,
  ParagraphSentence,
  ParagraphSentenceQuiz,
  Pdf,
  PdfSection,
  PdfTranslation,
  Period,
  Question,
  QuestionSection,
  Section,
  Sound,
  StaffMember,
  TeacherApplication,
  TeacherApplicationVideo,
  Text,
  TextTranslation,
  User,
  VocabSection,
  Word,
  WordTranslation,
  LessonBooking,
  BrandLogo,
} from 'app/constants/schemas';
import { ENGLISH } from 'app/constants/enums/Language';
import { ASIA_TOKYO } from 'app/constants/schemas/Timezone';
import { STANDARD } from 'app/constants/ExerciseTypes';
import { ADD_LIST_ELEMENT, REMOVE_LIST_ELEMENT, SET_ATTRIBUTE } from 'app/models/entities/constants';
import { ONE_HOUR_MS } from 'app/utils/date';
import { entityCreator, entityTypePayloadProperties } from 'app/models/entities/actions/next';

export { entityCreator, entityTypePayloadProperties };

/*
 * Optimistic action creator for adding an element to a collection field on an entity.
 *
 * @param {string} args.id
 * @param {function} args.type An entity constructor.
 * @param {string} args.type.typeName The typename on the entity constructor.
 * @param {unknown} args.value Value for the child.
 * @param {string} args.attribute Attribute name.
 * @param {number} [args.position] Sets the position in an ordered set.
 * @returns {object} Action payload.
 */
export const addListElement = createOptimisticAction(
  ADD_LIST_ELEMENT,
  ({ type, id, attribute, value, position }) => ({
    id,
    attribute,
    value,
    position,
    ...entityTypePayloadProperties(type),
  }),
);

/**
 * Optimistic action creator for removing an element to a collection field on an entity.
 *
 * @param {string} args.id
 * @param {function} args.type An entity constructor.
 * @param {string} args.type.typeName The typename on the entity constructor.
 * @param {unknown} args.value Value for the child.
 * @param {string} args.attribute Attribute name.
 * @returns {object} Action payload.
 */
export const removeListElement = createOptimisticAction(
  REMOVE_LIST_ELEMENT,
  ({ type, id, attribute, value }) => ({
    id,
    attribute,
    value,
    ...entityTypePayloadProperties(type),
  }),
);

/**
 * Optimistic action creator for setting the value on an entity attribute.
 *
 * @param {string} args.id
 * @param {function} args.type An entity constructor.
 * @param {string} args.type.typeName The typename on the entity constructor.
 * @param {string} args.attribute Attribute name.
 * @param {unknown} [args.value] Value to set on the field. `undefined` will unset the value.
 * @returns {object} Action payload.
 */
export const setAttribute = createOptimisticAction(
  SET_ATTRIBUTE,
  ({ type, id, attribute, value }) => ({
    id,
    attribute,
    value,
    ...entityTypePayloadProperties(type),
  }),
);

/**
 * Helper function for childAdder and childSetter.
 *
 * @param {function} create - Function for creating an entity.
 * @param {function} update - Function for updating a parent entity. Will be passed the
 * id of the child.
 * @returns - Batched actions that can be dispatched to a store.
 */
const createChildAndUpdateParent = (create, update) => {
  const createChildAction = create();
  const { id: child } = createChildAction;
  const addListElementAction = update(child);

  const batchedAction = batchActions([createChildAction, addListElementAction]);
  // Instead of using action.payload.id to get the new entity's id, use action.id because the
  // payload of batched actions is an array of actions, not a regular action payload.
  batchedAction.id = child;

  return batchedAction;
};

/**
 * Helper method for creating child associations and adding them to a collection on
 * the parent.
 *
 * @param {function} childActionCreator - Takes an action creator made by entityCreator.
 * @param {function} parentType - Entity constructor.
 * @param {string} parentType.typeName
 * @param {string} parentAttribute - The field name to add the resulting child ID to.
 * @returns {function} - Action creator for adding a child entity.
 */
export const childAdder = (childActionCreator, parentType, parentAttribute) =>
  /**
   * @param {object} childProperties
   * @param {string} id
   * @param {number} [position]
   * @returns - Batched actions to create the entity and update the parent.
   */
  (childProperties, id, position) => {
    const args = { type: parentType, attribute: parentAttribute, id, position };

    const create = () => childActionCreator(childProperties);
    const update = (value) => addListElement({ value, ...args });

    return createChildAndUpdateParent(create, update);
  };

/**
 * Helper method for creating child associations and setting them on a parent's attribute.
 *
 * @param {function} childActionCreator - Takes an action creator made by entityCreator.
 * @param {function} parentType - Entity constructor.
 * @param {string} parentType.typeName
 * @param {string} parentAttribute - The field name to add the resulting child ID to.
 * @returns {function} - Action creator for setting a child entity.
 */
const childSetter = (childActionCreator, parentType, parentAttribute) =>
  /**
   * @param {object} childProperties
   * @param {string} id
   * @returns - Batched actions to create the entity and update the parent.
   */
  (childProperties, id) => {
    const args = { type: parentType, attribute: parentAttribute, id };

    const create = () => childActionCreator(childProperties);
    const update = (value) => setAttribute({ value, ...args });

    return createChildAndUpdateParent(create, update);
  };

export const attributeSetter = (type, attribute) => (id, value) =>
   setAttribute({ type, attribute, id, value });

export const childRemover = (type, attribute) => (id, value) =>
  removeListElement({ type, attribute, id, value });

export const createText = entityCreator(Text, () => ({
  text: '',
}));
export const createTextTranslation = entityCreator(TextTranslation, () => ({
  translation: '',
}));
export const createPdfTranslation = entityCreator(PdfTranslation, () => ({ translation: '' }));

export const setTranslation = attributeSetter(TextTranslation, 'translation');
export const setWordTranslation = attributeSetter(WordTranslation, 'translation');

export const removePdfTranslation = childRemover(PdfSection, 'pdfTranslations');

export const createTextTranslationChild =
  childAdder(createTextTranslation, Text, 'textTranslations');

export const createCommonTextTranslationChild =
  childAdder(createTextTranslation, CommonText, 'textTranslations');

export const createPdfTranslationChild =
  childAdder(createPdfTranslation, PdfSection, 'pdfTranslations');

export const createExercise = entityCreator(Exercise, () => ({ exerciseType: STANDARD.id }));

export const createLessonExerciseChild = childAdder(createExercise, Lesson, 'exercises');

// This action creator takes care of Text children' life cycle.
//
// It will:
//
// - modify current instance of Text child if it exists
// - add a Text child if it doesn't exist
// - remove the Text child if value was is empty and the attribute is nullable
//
export const setTextChild = ({ id, type, attribute, notNull, textId, value }) => {
  if (textId && (value || notNull)) {
    // Set the value of the text entity if it exists or if the field
    // on the parent is not nullable. We also make sure `text`
    // attribute of the `Text` entity is never set to null.
    return setAttribute({ type: Text, attribute: 'text', id: textId, value: value || '' });
  } else if (!textId && value) {
    // If the text entity doesn't exist, create it
    const createTextAction = createText({ text: value });
    const setTextAction = setAttribute({ type, id, attribute, value: createTextAction.id });
    const batchedAction = batchActions([createTextAction, setTextAction]);

    batchedAction.id = textId;

    return batchedAction;
  } else {
    // If text is empty and nullable, null the field on parent
    return setAttribute({ id, type, attribute, value: null });
  }
};

// Media

export const createImage = entityCreator(Image, () => ({
  publicAttribution: true,
}));
export const createBrandLogo = entityCreator(BrandLogo);
export const createSound = entityCreator(Sound);
export const createPdf = entityCreator(Pdf);

export const setSound = attributeSetter('sound');
export const setImage = attributeSetter('image');

// Dialog sections

export const createDialogSection = entityCreator(DialogSection, child => ({
  visibility: 'All',
  dialogEntries: List([
    child(createDialogEntry()),
    child(createDialogEntry()),
  ]),
}));

export const createDialogEntry = entityCreator(DialogEntry, child => ({
  dialogSentences: List([child(createDialogSentence())]),
  stageDirection: false,
}));

export const createDialogSentence = entityCreator(DialogSentence, child => ({
  text: child(createText()),
}));

export const createDialogSectionChild =
  childAdder(createDialogSection, Exercise, 'sections');

export const createDialogSentenceChild =
  childAdder(createDialogSentence, DialogEntry, 'dialogSentences');

export const createDialogEntryChild =
  childAdder(createDialogEntry, DialogSection, 'dialogEntries');

/**
 * Add a dialog entry inside a dialog section. Uses the same dialog speaker as the dialog entry
 * before the last one.
 */
export const addDialogSectionEntry = ({ sectionId }) => (dispatch, getState) => {
  const state = getState();
  const section = getEntityOfTypeOrThrow(state, Section, sectionId);

  const nonStageDirectionEntries = section.dialogEntries.filter(entryId =>
    !getEntityOfTypeOrThrow(state, DialogEntry, entryId).stageDirection);

  const secondFromLastSpeakerId = nonStageDirectionEntries.get(-2) ?
    getEntityOfTypeOrThrow(state, DialogEntry, nonStageDirectionEntries.get(-2)).dialogSpeaker :
    null;

  return dispatch(createDialogEntryChild(
    { dialogSpeaker: secondFromLastSpeakerId },
    sectionId,
  ));
};

export const addDialogStageDirectionEntry = ({ sectionId }) =>
  createDialogEntryChild({ stageDirection: true }, sectionId);

export const createDialogSentenceQuiz = entityCreator(DialogSentenceQuiz, () => ({
  embedded: true,
  quizType: FREEFORM.id,
}));

export const createDialogSentenceQuizChild =
  childAdder(createDialogSentenceQuiz, DialogSentence, 'dialogSentenceQuizzes');

export const addQuizToDialogSentence = ({ exerciseId, dialogSentenceId }) =>
  (dispatch, getState) => {
    const createQuizAction = dispatch(createDialogSentenceQuizChild({}, dialogSentenceId));
    const newQuizId = createQuizAction.id;

    dispatch(initializeQuiz({
      exerciseId,
      quizId: newQuizId,
      quizEntityType: DialogSentenceQuiz,
    }));

    return createQuizAction;
  };

// Question sections

const createQuestionSection = entityCreator(QuestionSection, child => ({
  visibility: 'All',
  questions: List(),
}));

export const createQuestion = entityCreator(Question, child => ({
  text: child(createText()),
  quizType: FREEFORM.id,
  embedded: false,
}));

export const createAnswer = entityCreator(Answer, child => ({
  correct: false,
  text: child(createText()),
}));

export const setCorrect = attributeSetter(Answer, 'correct');

export const createQuestionSectionChild =
  childAdder(createQuestionSection, Exercise, 'sections');

export const addQuestionSectionToExercise = (fieldValues, exerciseId) => (dispatch, getState) => {
  const createQuestionSectionAction = dispatch(createQuestionSectionChild(fieldValues, exerciseId));
  const sectionId = createQuestionSectionAction.id;

  dispatch(createQuestionSectionQuestion({ exerciseId, sectionId }));

  return createQuestionSectionAction;
};

const createQuestionChild =
  childAdder(createQuestion, QuestionSection, 'questions');

export const createQuestionSectionQuestion = ({ exerciseId, sectionId }) =>
  (dispatch, getState) => {
    const createQuestionAction = dispatch(createQuestionChild({}, sectionId));
    const newQuestionId = createQuestionAction.id;

    dispatch(initializeQuiz({
      exerciseId,
      quizId: newQuestionId,
      quizEntityType: Question,
    }));

    return createQuestionAction;
  };

/**
 * Set's the quiz's default type to the same as the previous quiz in the section, if there is one,
 * and initializes the quizzes's answers. Defaults to FREEFORM if there is no previous quiz.
 */
const initializeQuiz = ({ exerciseId, quizId, quizEntityType }) => (dispatch, getState) => {
  const previousQuiz = findPreviousQuizInExercise(getState(), { exerciseId, quizId });
  const quizType = previousQuiz ? previousQuiz.quizType : FREEFORM.id;

  const initializeAnswersAction = initializeQuizAnswers({ quizType });

  dispatch(batchActions([
    initializeAnswersAction,
    setAttribute({
      id: quizId,
      type: quizEntityType,
      attribute: 'quizType',
      value: quizType,
    }),
    setAttribute({
      id: quizId,
      type: quizEntityType,
      attribute: 'answers',
      value: initializeAnswersAction.ids,
    }),
  ]));
};

const initializeQuizAnswers = ({ quizType }) => {
  // Automatically add two answers to multiple choice quiz questions, and one answer to pooled
  // answer questions.
  const numAnswersToAdd = (
    isMultiChoice(quizType) ? 2 :
    isPool(quizType) ? 1 :
    0
  );

  const createAnswerActions = Range(0, numAnswersToAdd).map(() => createAnswer()).toJS();
  const batchedActions = batchActions(createAnswerActions);
  batchedActions.ids = List(createAnswerActions.map(action => action.id));

  return batchedActions;
};

export const createQuizAnswer = ({ id, type }) => (dispatch, getState) => {
  const quizType = getEntityOfType(getState(), type, id).quizType;
  const createAnswerAction = createAnswer();
  const answerId = createAnswerAction.id;
  const addListElementAction = addListElement({ type, id, value: answerId, attribute: 'answers' });

  // Freeform question answers should always be correct.
  const batchedAction = quizType === FREEFORM.id ?
    batchActions([createAnswerAction, addListElementAction, setCorrect(answerId, true)]) :
    batchActions([createAnswerAction, addListElementAction]);

  batchedAction.id = answerId;
  return dispatch(batchedAction);
};

export const setQuestionText = ({ id, value }) => (dispatch, getState) => {
  const hasQuizMarker = findNumOccurrences(value, FIB_EMBEDDING_MARKER) >= 1;
  const textId = getEntityOfType(getState(), Question, id).text;

  return dispatch(batchActions([
    setTextChild({
      id,
      type: Question,
      attribute: 'text',
      textId,
      notNull: Question.associations.text.notNull,
      value,
    }),
    setAttribute({
      id,
      type: Question,
      attribute: 'embedded',
      value: hasQuizMarker,
    }),
  ]));
};

export const setQuizType = ({ id, type, quizType }) => (dispatch, getState) => {
  const entity = getEntityOfType(getState(), type, id);
  const changeQuizTypeAction = setAttribute({
    id,
    type,
    attribute: 'quizType',
    value: quizType || null,
  });

  // Freeform question answers should always be correct.
  if (quizType === FREEFORM.id) {
    const makeAnswersCorrectActions =
      entity.answers.map((answerId) => setCorrect(answerId, true));
    dispatch(batchActions([changeQuizTypeAction, ...makeAnswersCorrectActions]));
  } else {
    dispatch(changeQuizTypeAction);
  }
};

const createParagraphSentenceQuiz = entityCreator(ParagraphSentenceQuiz, () => ({
  embedded: true,
  quizType: FREEFORM.id,
}));

export const createParagraphSentenceQuizChild =
  childAdder(createParagraphSentenceQuiz, ParagraphSentence, 'paragraphSentenceQuizzes');

export const addQuizToParagraphSentence = ({ exerciseId, paragraphSentenceId }) =>
  (dispatch, getState) => {
    const createQuizAction = dispatch(createParagraphSentenceQuizChild({}, paragraphSentenceId));
    const newQuizId = createQuizAction.id;

    dispatch(initializeQuiz({
      exerciseId,
      quizId: newQuizId,
      quizEntityType: ParagraphSentenceQuiz,
    }));

    return createQuizAction;
  };

const makeSetQuizText = ({
  parentType,
  quizzesField,
  textField,
  addQuizAction,
}) => ({ id, value, exerciseId }) => (dispatch, getState) => {
  const entity = getEntityOfType(getState(), parentType, id);
  const textId = entity[textField];

  const setTextAction = setTextChild({
    id,
    type: parentType,
    attribute: textField,
    textId,
    notNull: parentType.associations[textField].notNull,
    value,
  });

  const numQuizzes = entity[quizzesField].size;
  const getNumQuizMarkers = makeGetNumQuizMarkers(() => value);
  const numQuizMarkers = getNumQuizMarkers();
  const numQuizzesNeeded = Math.max(0, numQuizMarkers - numQuizzes);

  dispatch(setTextAction);

  // Add quizzes if there are more quiz markers than quizzes.
  Range(0, numQuizzesNeeded).forEach(() =>
    dispatch(addQuizAction({ exerciseId, parentId: id })),
  );
};

export const setParagraphSentenceText = makeSetQuizText({
  parentType: ParagraphSentence,
  textField: 'text',
  quizzesField: 'paragraphSentenceQuizzes',
  addQuizAction: ({ exerciseId, parentId }) =>
    addQuizToParagraphSentence({ exerciseId, paragraphSentenceId: parentId }),
});

export const setDialogSentenceText = makeSetQuizText({
  parentType: DialogSentence,
  textField: 'text',
  quizzesField: 'dialogSentenceQuizzes',
  addQuizAction: ({ exerciseId, parentId }) =>
    addQuizToDialogSentence({ exerciseId, dialogSentenceId: parentId }),
});

// Vocab sections

export const createVocabSection = entityCreator(VocabSection, () => ({
  visibility: 'All',
  vocabType: SIMPLE.id,
  words: List([]),
}));

export const setWord = attributeSetter(Word, 'word');
export const setLocalWord = attributeSetter(LocalWord, 'word');
export const setPartOfSpeech = attributeSetter(Word, 'partOfSpeech');

export const createVocabSectionChild =
  childAdder(createVocabSection, Exercise, 'sections');

// Media sections

const initializeMediaList = child => ({
  visibility: 'All',
  mediaSize: MEDIUM.id,
  mediaEntries: List([child(createMediaEntry())]),
});

export const createMediaSection = entityCreator(MediaSection, initializeMediaList);

export const createMediaSectionChild =
  childAdder(createMediaSection, Exercise, 'sections');

export const createMediaGroup = entityCreator(MediaGroup, initializeMediaList);

export const setMedia = attributeSetter('media');
export const setCaptionText = attributeSetter('captionText');

export const createParagraphLeadingMediaGroup =
  childSetter(createMediaGroup, Paragraph, 'leadingMediaGroup');
export const createParagraphTrailingMediaGroup =
  childSetter(createMediaGroup, Paragraph, 'trailingMediaGroup');

export const createMediaEntry = entityCreator(MediaEntry);

export const addMediaEntry = ({ type, id, position }) => {
  const args = { type, attribute: 'mediaEntries', id, position };

  const create = () => createMediaEntry({});
  const update = (value) => addListElement({ value, ...args });

  return createChildAndUpdateParent(create, update);
};

export const createMediaEntrySound = childSetter(createSound, MediaEntry, 'sound');
export const createMediaEntryImage = childSetter(createImage, MediaEntry, 'image');

export const removeMediaEntrySound = entryId =>
  setAttribute({ id: entryId, type: MediaEntry, value: null, attribute: 'sound' });
export const removeMediaEntryImage = entryId =>
  setAttribute({ id: entryId, type: MediaEntry, value: null, attribute: 'image' });

// Article sections
export const createArticleSection = entityCreator(ArticleSection, child => ({
  visibility: 'All',
  paragraphs: List([child(createParagraph())]),
}));

export const createParagraph = entityCreator(Paragraph, child => ({
  paragraphSentences: List([child(createParagraphSentence())]),
  paragraphStyle: PLAIN,
}));

export const createParagraphSentence = entityCreator(ParagraphSentence, child => ({
  text: child(createText()),
}));

export const createArticleSectionChild =
  childAdder(createArticleSection, Exercise, 'sections');

export const createParagraphChild =
  childAdder(createParagraph, ArticleSection, 'paragraphs');

export const createParagraphSentenceChild =
  childAdder(createParagraphSentence, Paragraph, 'paragraphSentences');

// Pdf Sections

export const createPdfSection = entityCreator(PdfSection);
export const createPdfSectionChild = childAdder(createPdfSection, Exercise, 'sections');

// Issues

export const createIssue = entityCreator(Issue);

// Users

export const createUser = entityCreator(
  User,
  (user) => ({
    email: '',
    language: ENGLISH,
    timezone: ASIA_TOKYO,
  }),
  () => ({
    [BASE_ACTION]: true,
  }),
);

// Staff

export const createStaffMember = entityCreator(
  StaffMember,
  () => ({
    enabled: false,
  }),
);

// Basic Sections

export const createBasicSection = entityCreator(Section, () => ({
  visibility: 'All',
}));

export const createBasicSectionChild = childAdder(createBasicSection, Exercise, 'sections');

// Banners

export const createBanner = entityCreator(
  Banner,
  (child) => {
    // Set min to be at the beginning of day (in UTC).
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    const min = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));

    return {
      bannerContext: child(createBannerContext()),
      period: new Period({ min, max: null }),
      weight: 50,
    };
  },
  () => ({
    [BASE_ACTION]: true,
  }),
);

const createBannerContext = entityCreator(
  BannerContext,
  (context) => ({
    studyLanguages: List([ENGLISH.id]),
  }),
  () => ({
    [BASE_ACTION]: true,
  }),
);

export const createBannerImage = entityCreator(BannerImage);
export const createBannerProduct = entityCreator(BannerProduct);
export const createBannerTranslation = entityCreator(BannerTranslation, () => ({ target: '' }));

export const createOauthApplication = entityCreator(OauthApplication);

export const createLessonBooking = entityCreator(
  LessonBooking,
  () => {
    const now = new Date(Date.now());

    now.setSeconds(0);
    now.setMilliseconds(0);

    const oneHourFromNow = new Date(now.valueOf() + ONE_HOUR_MS);

    return {
      // Initialize period to something that starts now and doesn't end soon.
      time: new Period({
        min: now,
        max: oneHourFromNow,
      }),
    };
  },
  () => ({
    [BASE_ACTION]: true,
  }),
);

// Teacher application

export const createTeacherApplication = entityCreator(TeacherApplication);
export const createTeacherApplicationVideo = entityCreator(TeacherApplicationVideo);
export const makeSetTeacherApplicationVideo =
  (videoAttributeName) => childSetter(createTeacherApplicationVideo, TeacherApplication, videoAttributeName);
